import React, { useState } from "react";
import PaginationBar from "../ui/PaginationBar";
import { usePagination } from "../../hooks/usePagination";
import AdminLayout from "../layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { FetchLogs, AdminUpdateLog } from "../../services/api";
import { Button } from "../ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const LoginActivity = ({ Layout, canManage }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const LayoutComp = Layout || AdminLayout;
  const isSuperAdmin = canManage ?? user?.role === "SUPER_ADMIN";

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: logs = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["loginLogs", dateFrom, dateTo],
    queryFn: () =>
      FetchLogs({
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      }),
  });

  const { page, pageSize, total, pageItems, setPage, setPageSize } = usePagination(logs);

  const clearLogsMutation = useMutation({
    mutationFn: () => AdminUpdateLog({ action: "clear_all" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loginLogs"] });
      alert("Audit log cleared successfully");
    },
    onError: (err) => {
      alert("Failed to clear logs: " + (err?.response?.data?.detail || err.message || "Unknown error"));
    },
  });

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  return (
    <LayoutComp>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
            <p className="text-gray-600 mt-1">
              System-wide audit trail of Admin and Super Admin logins.
              {!isSuperAdmin && " (Read-only)"}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm">
          <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <Button variant="outline" onClick={clearFilters} className="h-10">
              Clear Filters
            </Button>
            <Button variant="outline" onClick={() => refetch()} className="h-10" disabled={isFetching}>
              {isFetching ? "Refreshing..." : "Refresh"}
            </Button>

            {isSuperAdmin && (
              <Button
                variant="destructive"
                className="h-10 ml-auto"
                onClick={() => {
                  if (window.confirm("Are you sure you want to clear the entire audit log? This cannot be undone.")) {
                    clearLogsMutation.mutate();
                  }
                }}
                disabled={clearLogsMutation.isLoading || isLoading || logs.length === 0}
              >
                {clearLogsMutation.isLoading ? "Clearing..." : "Clear Audit Log"}
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="p-12 text-center text-gray-500">Loading audit log...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              Failed to load audit log: {error.message || "Unknown error"}
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No login activity recorded for this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Browser / Device</th>
                    <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pageItems.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{log.user_name}</p>
                        <p className="text-xs text-gray-400">{log.user_email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          log.user_role === "SUPER_ADMIN" ? "bg-purple-100 text-purple-700" : "bg-green-100 text-green-700"
                        }`}>
                          {log.user_role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900">{log.browser || "Unknown"}</td>
                      <td className="px-6 py-4 text-gray-600 font-mono text-sm">{log.ip || "—"}</td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(log.time).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {logs.length > 0 && (
            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun="login records"
            />
          )}
        </div>
      </div>
    </LayoutComp>
  );
};

export default LoginActivity;
